/**
 * Transducer registration vocabulary (Bloco 17).
 *
 * TIPO and FREQUÊNCIA are independent fields: "5 MHz" is a frequency and must
 * never appear as a type. Both offer a standard list plus an explicit
 * "OUTRO"/"OUTRA" escape that stores the value the user typed — nothing is
 * inferred or invented.
 */

export const TRANSDUCER_TYPES = [
  "DUPLO CRISTAL",
  "CRISTAL SIMPLES",
  "RETO / ONDA LONGITUDINAL",
  "LINHA DE RETARDO",
  "IMERSÃO",
  "ALTA TEMPERATURA",
  "OUTRO"
] as const;

export type TransducerType = (typeof TRANSDUCER_TYPES)[number];

export const TRANSDUCER_TYPE_OTHER = "OUTRO";

/** Standard ultrasonic frequencies, in MHz. "OUTRA" unlocks a custom decimal value. */
export const TRANSDUCER_FREQUENCIES_MHZ = [1.0, 2.0, 2.25, 2.5, 3.5, 4.0, 5.0, 7.5, 10.0, 15.0] as const;

export const TRANSDUCER_FREQUENCY_OTHER = "OUTRA";

export const TRANSDUCER_DIMENSION_UNITS = ["mm", "pol"] as const;

export type TransducerDimensionUnit = (typeof TRANSDUCER_DIMENSION_UNITS)[number];

/** Default application for the transducers consumed by the ME report. */
export const TRANSDUCER_DEFAULT_APPLICATION = "ME-US / ULTRASSOM";

export function isOtherTransducerType(value: string | null | undefined): boolean {
  return (value || "").trim().toUpperCase() === TRANSDUCER_TYPE_OTHER;
}

/** "5,0 MHz" — pt-BR decimal comma, empty when no frequency is registered. */
export function formatTransducerFrequency(frequencyMHz?: number | null): string {
  if (frequencyMHz === null || frequencyMHz === undefined) return "";
  if (!Number.isFinite(frequencyMHz)) return "";
  const text = Number.isInteger(frequencyMHz) ? `${frequencyMHz},0` : String(frequencyMHz).replace(".", ",");
  return `${text} MHz`;
}

/** "Ø 11 mm" — empty when no dimension is registered. */
export function formatTransducerDimension(value?: number | string | null, unit?: string | null): string {
  if (value === null || value === undefined || value === "") return "";
  const normalizedUnit = (unit || "mm").trim();
  const numeric = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  const text = Number.isFinite(numeric) ? String(numeric).replace(".", ",") : String(value);
  return `Ø ${text} ${normalizedUnit}`;
}

/** Whether a standard frequency list entry matches the registered value (so the select can pre-select it). */
export function isStandardFrequency(frequencyMHz?: number | null): boolean {
  if (frequencyMHz === null || frequencyMHz === undefined) return false;
  return (TRANSDUCER_FREQUENCIES_MHZ as readonly number[]).includes(frequencyMHz);
}
